import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';

import { firestore } from '../firebase';
import { pacificDayString } from '../../shared/dates';
import type { DocumentData } from 'firebase/firestore';

import { dailyPromptSchema, parseDoc, punSchema, userStatsSchema } from '../../shared/schemas';
import { formatCountdownPadded, secondsUntilPacificMidnight } from '../lib/countdown';
import type { AppState, DailyPrompt } from '../lib/appState';
import { reconcileDeviceToken } from '../lib/notifications';

/**
 * The chrome every signed-in screen sits inside: a masthead, an outlet, and
 * three tabs pinned to the bottom.
 *
 * Three tabs, not the previous seven destinations. The old app had four tabs
 * inside the dashboard plus a settings dropdown holding My Puns and How It
 * Works, neither of which is a setting, plus a /friends route duplicating one
 * of the tabs. Everything that is not writing a pun or reading the feed now
 * lives under You.
 *
 * Bottom rather than top because this is used on phones and that is where
 * thumbs are.
 */
function AppShell({ user }: { user: User }) {
  const [prompt, setPrompt] = useState<DailyPrompt | null>(null);
  const [promptFailed, setPromptFailed] = useState(false);
  const [submittedPun, setSubmittedPun] = useState<string | null>(null);
  const [checkingSubmission, setCheckingSubmission] = useState(true);
  const [submissionCheckFailed, setSubmissionCheckFailed] = useState(false);
  /** Bumped to re-run the submission check after a failure. */
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [crownCount, setCrownCount] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntilPacificMidnight());

  // Live, so a tab left open across midnight picks up the new prompt.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(firestore, 'dailyCategories', 'current'),
      (snapshot) => {
        if (!snapshot.exists()) {
          setPromptFailed(true);
          return;
        }

        // A malformed document should cost a retry, not throw out of the
        // listener and take the render with it.
        const parsed = dailyPromptSchema.safeParse(snapshot.data());
        if (!parsed.success) {
          console.error('[AppShell] prompt did not parse:', parsed.error);
          setPromptFailed(true);
          return;
        }

        setPrompt({
          category: parsed.data.category,
          output: parsed.data.output,
          aiPun: parsed.data.aiGeneratedPun ?? '',
          aiStatus: parsed.data.aiGenerationStatus ?? null,
        });
        setPromptFailed(false);
      },
      (error) => {
        console.error('[AppShell] prompt listener failed:', error);
        setPromptFailed(true);
      },
    );

    return unsubscribe;
  }, []);

  /*
   * Have you posted today?
   *
   * The fast path is a point read at `puns/{uid}_{pacificDay}`, the id
   * `submitPun` writes. That answers it in one round trip and needs no index.
   *
   * The fallback exists because a miss on that id is not proof you have not
   * posted — it is only proof that no document sits at the id *this browser*
   * built. Puns written before the callable shipped have random ids, and any
   * disagreement between the client's day string and the server's produces the
   * same silent miss. `submitPun` already refuses a second pun on the strength
   * of this exact query, so trusting the point read alone let the two disagree:
   * the feed said you had not posted, and the server said you had.
   *
   * Covered by (userId, pstDay) in firestore.indexes.json.
   */
  useEffect(() => {
    let cancelled = false;

    const textOf = (snapshot: { id: string; data: () => DocumentData }) =>
      // A pun that exists but will not parse still means you have posted.
      // Treating it as "not posted" would invite a duplicate the server would
      // only reject. parseDoc supplies the id, which `data()` never carries.
      parseDoc(punSchema, snapshot.id, snapshot.data(), 'AppShell')?.text ?? '';

    const check = async () => {
      setCheckingSubmission(true);
      setSubmissionCheckFailed(false);

      try {
        const day = pacificDayString();

        const direct = await getDoc(doc(firestore, 'puns', `${user.uid}_${day}`));
        if (cancelled) return;

        if (direct.exists()) {
          setSubmittedPun(textOf(direct));
          return;
        }

        const matches = await getDocs(
          query(
            collection(firestore, 'puns'),
            where('userId', '==', user.uid),
            where('pstDay', '==', day),
            limit(1),
          ),
        );
        if (cancelled) return;

        if (matches.empty) {
          setSubmittedPun(null);
          return;
        }

        console.warn(
          `[AppShell] today's pun is at ${matches.docs[0].id}, not ${user.uid}_${day}`,
        );
        setSubmittedPun(textOf(matches.docs[0]));
      } catch (error) {
        if (cancelled) return;
        // Not "you have not posted". We do not know, and saying we do is what
        // locked people out of the feed.
        console.error('[AppShell] could not check today\'s submission:', error);
        setSubmissionCheckFailed(true);
      } finally {
        if (!cancelled) setCheckingSubmission(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [user.uid, checkAttempt]);

  /*
   * Read only. The old header recalculated this from every pun the user had
   * ever written and wrote the result back, which `firestore.rules` now denies
   * outright: userStats is maintained by the updateCrownCount trigger and is
   * `allow write: if false` for clients.
   */
  useEffect(() => {
    let cancelled = false;

    getDoc(doc(firestore, 'userStats', user.uid))
      .then((snapshot) => {
        if (cancelled || !snapshot.exists()) return;
        setCrownCount(userStatsSchema.parse(snapshot.data()).crownCount);
      })
      .catch((error) => console.error('[AppShell] could not read crown count:', error));

    return () => {
      cancelled = true;
    };
  }, [user.uid]);

  useEffect(() => {
    const id = setInterval(() => setSecondsLeft(secondsUntilPacificMidnight()), 1000);
    return () => clearInterval(id);
  }, []);

  // A no-op for anyone who has not granted notification permission. For anyone
  // who has, this catches a token that rotated since the last time a toggle
  // was flipped in Settings — see notifications.ts for why that otherwise
  // leaves the account holding two live tokens for one device.
  useEffect(() => {
    reconcileDeviceToken(user.uid).catch((error) =>
      console.error('[AppShell] could not reconcile device token:', error),
    );
  }, [user.uid]);

  const onSubmitted = useCallback((text: string) => setSubmittedPun(text), []);
  const recheckSubmission = useCallback(() => setCheckAttempt((n) => n + 1), []);

  const state: AppState = {
    userId: user.uid,
    prompt,
    promptFailed,
    submittedPun,
    checkingSubmission,
    submissionCheckFailed,
    recheckSubmission,
    onSubmitted,
    crownCount,
    secondsLeft,
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-40 border-b-2 border-ink bg-paper/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-2xl items-center justify-between px-5">
          <span className="font-display text-2xl font-extrabold tracking-tight">Pun &amp; Done</span>

          <div className="flex items-center gap-2">
            <span
              title="Crowns your puns have collected"
              className="rounded-full border-2 border-ink bg-mustard px-3 py-1 text-sm font-bold tabular-nums"
            >
              {crownCount} <span aria-hidden="true">&#128081;</span>
              <span className="sr-only">crowns</span>
            </span>
            {/*
              Fixed width, not padding alone. This repaints every second, and a
              pill that changes size mid-tick shoves the crown counter beside it
              around. `tabular-nums` is kept but cannot be relied on by itself —
              it is a no-op unless the font ships tabular figures.
            */}
            <span
              title="Until the prompt changes"
              className="w-[4.75rem] shrink-0 rounded-full border-2 border-ink bg-lagoon px-2 py-1 text-center text-xs font-semibold tabular-nums text-white"
            >
              {formatCountdownPadded(secondsLeft)}
            </span>
          </div>
        </div>
      </header>

      {/* pb clears the fixed tab bar plus the iOS home indicator. */}
      <main className="mx-auto w-full max-w-2xl px-5 pb-32 pt-8">
        <Outlet context={state} />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-ink bg-paper pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex w-full max-w-2xl">
          <Tab to="/today" label="Today">
            <path d="M12 3v18M3 12h18" />
          </Tab>
          <Tab to="/feed" label="Feed">
            <path d="M4 6h16M4 12h16M4 18h10" />
          </Tab>
          <Tab to="/you" label="You">
            <path d="M12 11a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0" />
          </Tab>
        </div>
      </nav>
    </div>
  );
}

/** Icons are inline paths so the tab bar owns its own sizing and stroke. */
function Tab({ to, label, children }: { to: string; label: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex flex-1 flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${
          isActive ? 'text-ink' : 'text-ink/40'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all ${
              isActive
                ? 'border-ink bg-coral text-white shadow-pop-sm'
                : 'border-transparent text-ink/40'
            }`}
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {children}
            </svg>
          </span>
          {label}
        </>
      )}
    </NavLink>
  );
}

export default AppShell;
